import { memo, useMemo, type ReactNode } from 'react';
import { parseInlineTerms } from '../utils/inline-terms';
import styles from './phone-mail.module.css';

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function termMatchesZoom(term: string, zoomTerms: string[]): boolean {
  const normalized = term.toLowerCase();
  return zoomTerms.some((zoomTerm) => zoomTerm.toLowerCase() === normalized);
}

function renderPlainWithZoom(
  text: string,
  zoomTerms: string[],
  zoomActive: boolean,
  keyPrefix: string
): ReactNode[] {
  if (!zoomActive || !zoomTerms.length) return [text];

  const nodes: ReactNode[] = [];
  let remaining = text;
  let zoomIndex = 0;

  while (remaining.length > 0) {
    let earliest: { index: number; length: number } | null = null;

    for (const term of zoomTerms) {
      if (!term) continue;
      const match = new RegExp(escapeRegex(term), 'i').exec(remaining);
      if (match?.index !== undefined && (earliest === null || match.index < earliest.index)) {
        earliest = { index: match.index, length: match[0].length };
      }
    }

    if (!earliest) {
      nodes.push(remaining);
      break;
    }

    if (earliest.index > 0) {
      nodes.push(remaining.slice(0, earliest.index));
    }

    const matchText = remaining.slice(earliest.index, earliest.index + earliest.length);
    nodes.push(
      <span
        key={`${keyPrefix}-zoom-${zoomIndex++}`}
        className={styles.emailZoomSource}
        data-email-zoom="1"
      >
        {matchText}
      </span>
    );
    remaining = remaining.slice(earliest.index + earliest.length);
  }

  return nodes;
}

export interface FormattedEmailContentProps {
  text: string;
  zoomTerms?: string[];
  zoomMarkersActive?: boolean;
  onHintChange?: (hint: string | null) => void;
}

export const FormattedEmailContent = memo(({
  text,
  zoomTerms = [],
  zoomMarkersActive = false,
  onHintChange,
}: FormattedEmailContentProps) => {
  const segments = useMemo(() => parseInlineTerms(text), [text]);

  const nodes = useMemo(() => {
    const rendered: ReactNode[] = [];
    let termIndex = 0;

    for (const segment of segments) {
      if (segment.type === 'plain') {
        rendered.push(
          ...renderPlainWithZoom(
            segment.text,
            zoomTerms,
            zoomMarkersActive,
            `plain-${termIndex}`
          )
        );
        continue;
      }

      const index = termIndex++;
      const isZoom = zoomMarkersActive && termMatchesZoom(segment.term, zoomTerms);
      const hintClass = segment.definition ? styles.emailHint : styles.emailHintOnly;

      rendered.push(
        <span
          key={`term-${index}`}
          className={[hintClass, isZoom ? styles.emailZoomSource : ''].filter(Boolean).join(' ')}
          data-email-zoom={isZoom ? '1' : undefined}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseEnter={segment.definition ? () => onHintChange?.(segment.definition!) : undefined}
          onMouseLeave={segment.definition ? () => onHintChange?.(null) : undefined}
          onFocus={segment.definition ? () => onHintChange?.(segment.definition!) : undefined}
          onBlur={segment.definition ? () => onHintChange?.(null) : undefined}
          tabIndex={segment.definition ? 0 : undefined}
        >
          {segment.term}
        </span>
      );
    }

    return rendered;
  }, [segments, zoomTerms, zoomMarkersActive, onHintChange]);

  return <>{nodes}</>;
});

FormattedEmailContent.displayName = 'FormattedEmailContent';
